<template>
  <section
    class="flex min-w-0 max-w-full items-center rounded-8 gap-3 cursor-pointer p-2 hover:bg-bg-base-bg3 group transition-all"
    :class="{ 'bg-bg-base-bg4': isSelected }"
    @mouseenter="show"
    @mouseleave="hide"
    @contextmenu.prevent="showContext($event)"
  >
    <contact-avatar
      class="flex-none"
      :class="{ 'opacity-60': isDeleted }"
      :image="avatar"
      :name="name"
      :is-room="true"
    />
    <div class="grid min-w-0 grow grid-rows-2 gap-y-1 overflow-hidden">
      <p
        class="truncate text-heading-m-16-22"
        :class="isDeleted ? 'text-fg-base-fg22' : 'text-fg-base-fg3'"
      >
        {{ name }}
      </p>
      <div class="flex min-w-0 items-center gap-x-2 overflow-hidden text-label-r-13-18 text-fg-base-fg22">
        <template v-if="isDeleted">
          <wui-icon
            name="trashM"
            class="shrink-0 text-warning"
            small
          />
          <p
            class="min-w-0 truncate tracking-[0.4px] text-warning"
            data-test="deleted-conference-label"
          >
            {{ t('calls.conferenceDeletedLabel') }}
          </p>
        </template>
        <template v-else>
          <wui-icon
            :name="iconName"
            :class="iconColor"
            class="shrink-0"
            small
          />
          <p
            class="min-w-0 truncate tracking-[0.4px]"
            :class="{ 'text-black-600': isSelected }"
          >
            {{ name }}
          </p>
        </template>
      </div>
    </div>

    <div class="relative flex flex-none shrink-0 items-center justify-end w-24 h-11">
      <div
        class="text-label-r-13-18 text-fg-base-fg22 absolute inset-0 grid grid-rows-2 content-center justify-items-end gap-y-2 overflow-hidden transition-opacity"
        :class="[
          { 'opacity-0 invisible': hover && !callInProgress },
          { 'text-black-600': isSelected },
        ]"
      >
        <p class="min-w-0 max-w-full truncate whitespace-nowrap">
          {{ date }}
        </p>
        <p class="min-w-0 max-w-full truncate whitespace-nowrap tracking-[0.4px]">
          {{ duration }}
        </p>
      </div>
      <wui-btn
        v-if="webSoftphoneEnabled && hover && !callInProgress && !isDeleted"
        class="relative z-10"
        :title="`${canMakeCall ? $t('common.makeCall') : $t('mainAside.cannotInitiateCall')}`"
        :aria-label="`${canMakeCall ? $t('common.makeCall') : $t('mainAside.cannotInitiateCall')}`"
        :disabled="!canMakeCall"
        variant="brand"
        state="tonal"
        icon
        auto-loading
        prepend-icon="phoneM"
        @click.stop="openPrepareToCallPage"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { WuiIcon, IconName, WuiBtn } from '@wui/common-library'
import { ConferenceType } from '@wui/im'
import { computed } from 'vue'
import { useRouter } from 'vue-router'

import { ContactAvatar } from '@/entities/contact-avatar'
import { useStoreRooms } from '@/entities/room'
import { RTCSessionFacade } from '@/entities/webrtc'

import { getCallDuration } from '@/shared/composables/call-duration'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useHover } from '@/shared/composables/use-hover'
import { useWebSoftphoneEnabled } from '@/shared/composables/user-features'
import { Room } from '@/shared/types/store'
import { PREPARE_TO_CALL } from '@/shared/utils/constants'
import { getScheduleEventDisplayName } from '@/shared/utils/schedule'
import { getFullURL } from '@/shared/utils/url'

import {
  findLiveCallSession,
  getCallForwardedDisplayLabel,
  isCallForwardedEvent,
  isDeletedConferenceCall,
} from '../lib'
import { CallEventWithStatus } from '../types'

const props = defineProps<{
  item: CallEventWithStatus
  roomId: string
  iconName: IconName
  iconColor: string
  date: string
  isSelected: boolean
  liveCallSessions: RTCSessionFacade[]
}>()

const router = useRouter()
const emits = defineEmits<{
  (event: 'show-context', e: MouseEvent | TouchEvent, room: Room): void
}>()

const showContext = (event: MouseEvent | TouchEvent) => {
  if (room.value && !isDeleted.value) {
    emits('show-context', event, room.value)
  }
}

const { hover, show, hide } = useHover()
const { webSoftphoneEnabled } = useWebSoftphoneEnabled()

const storeRooms = useStoreRooms()
const { currentUser } = useCurrentUser()
const { t } = useLocalization()

const room = computed(() => storeRooms.getRoomById(props.roomId))
const isDeleted = computed(() => isDeletedConferenceCall('room', room.value))
const name = computed(() => {
  const rawName = room.value?.name ?? ('roomName' in props.item ? props.item.roomName : undefined) ?? props.roomId
  const baseName = getScheduleEventDisplayName(rawName, props.roomId)
  if (isCallForwardedEvent(props.item)) {
    return getCallForwardedDisplayLabel(props.item, baseName, currentUser.value?.imLogin)
  }
  return baseName
})
const avatar = computed(() => room.value?.avatar ? getFullURL(room.value.avatar) : undefined)
const activeSession = computed(() => findLiveCallSession(props.liveCallSessions, { roomNumber: props.roomId }))
const callInProgress = computed(() => !!activeSession.value)

const canMakeCall = computed(() => {
  const contact = room.value?.contacts?.find(c => c.id === currentUser.value?.imLogin)
  return room.value?.mode === ConferenceType.FREE_FOR_ALL || (room.value?.mode === ConferenceType.MODERATED && (contact?.role === 'Moderator' || contact?.role === 'Owner'))
})

const openPrepareToCallPage = async () => {
  await router.push({ name: PREPARE_TO_CALL, params: { roomId: props.roomId }})
}

const duration = computed(() => {
  if (props.item.event === 'call_forwarded' && 'callForwardDestination' in props.item) {
    return t('common.callForwardReason.' + props.item.callForwardReason)
  }
  return getCallDuration(props.item)
})
</script>
